// Thin fetch wrapper for /api/* calls. Sprint 11b: supports both same-origin
// (single-server — the shape As Oasis uses since zt4b/INV-19) and
// cross-origin (the multi-server federation path, still supported in code).
// The baseUrl is resolved from the active server in useServersStore; when
// empty, paths are relative (same-origin).

import type { Agent, ApiError, EffortLevel, Message, PermissionMode, WorkdirIndexResponse } from '../types';
import { getActiveBaseUrl } from '../stores/servers.store';

export class ApiHttpError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

// D1 (sprint access-session-recovery, spec §2 D1) — Cloudflare Access gate
// detection. `redirect: 'manual'` turns the edge's 302 into an inert,
// browser-filtered `opaqueredirect` response instead of either (a) a
// followed redirect no XHR/fetch can complete anyway (ZTH-4 PM-6) or (b) a
// generic `TypeError` indistinguishable from "no network" (the exact
// confusion behind D013-EC-560). ONE helper owns both the init flag and the
// predicate (challenger R1 change 1): three independent copies of this
// check across `api()`, `uploadAttachment` and `DocViewerSheet` would be
// the same defect D013-EC-560 already produced once, in miniature.
export const ACCESS_GATE_FETCH_INIT: RequestInit = { redirect: 'manual' };

/**
 * True when `res` is the browser's opaque stand-in for a same-origin fetch
 * that hit a cross-origin (Cloudflare Access) redirect. This is the ONLY
 * signal read — no `navigator.onLine`, no `res.type !== 'basic'`, no bare
 * `status === 0` (R11: those also fire for ordinary CORS-opaque responses
 * that have nothing to do with Access). MUST be checked before any attempt
 * to read the response body — a body read on an opaqueredirect response
 * degrades the classification to a bare `HTTP_ERROR`/`HTTP 0` (R10).
 */
export function isAccessGateRedirect(res: Response): boolean {
  return res.type === 'opaqueredirect';
}

/**
 * True when `err` is the classified form of an Access-gate rejection —
 * either the client-side opaqueredirect above (surfaced as
 * `ApiHttpError(0, 'ACCESS_GATE', …)`) or the origin's
 * `cloudflare-access.middleware.ts` 403 `ACCESS_DENIED` (when the request
 * reaches the server without a valid JWT). `useAuth`'s `gate-expired`
 * branch is the one reader of this predicate (D1/D2).
 */
export function isAccessGateError(err: unknown): boolean {
  if (!(err instanceof ApiHttpError)) return false;
  if (err.code === 'ACCESS_GATE') return true;
  return err.status === 403 && err.code === 'ACCESS_DENIED';
}

async function parseError(res: Response): Promise<ApiHttpError> {
  let code = 'HTTP_ERROR';
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as Partial<ApiError>;
    if (body && typeof body === 'object' && body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
    }
  } catch {
    // not JSON, keep defaults
  }
  return new ApiHttpError(res.status, code, message);
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  signal?: AbortSignal;
  /** Override the base URL for this single request. */
  baseUrl?: string;
}

// Sprint 5.5 (H3): the backend rejects state-changing requests that don't
// carry this header. Browsers refuse to attach custom headers on cross-site
// form/img/etc. requests, so it doubles as CSRF protection for the cookie
// session. Sent on EVERY request (GETs included) — cheap and uniform.
const CSRF_HEADER = 'X-Dashboard-Request';

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, baseUrl } = opts;
  const base = baseUrl ?? getActiveBaseUrl();
  const headers: Record<string, string> = { [CSRF_HEADER]: '1' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...ACCESS_GATE_FETCH_INIT,
  };
  if (signal) init.signal = signal;

  let res: Response;
  try {
    res = await fetch(`${base}${path}`, init);
  } catch (e) {
    // D1 — with `redirect: 'manual'` in play the Access gate no longer
    // throws (it resolves to an opaqueredirect response, handled below), so
    // a thrown error here means the request genuinely never reached a
    // server: DNS failure, offline, CORS. AbortError is a caller-requested
    // cancellation, not a network failure — `fetchRunnersUsage`,
    // `fetchWorkdirIndex` and `useAutocomplete` all pass AbortSignals and
    // rely on catching it by name, so it must propagate unchanged rather
    // than being relabelled `NETWORK_ERROR`.
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiHttpError(0, 'NETWORK_ERROR', 'Could not reach the server.');
  }

  if (isAccessGateRedirect(res)) {
    throw new ApiHttpError(0, 'ACCESS_GATE', 'Access gate redirect.');
  }
  if (!res.ok) throw await parseError(res);

  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return undefined as T;
  return (await res.json()) as T;
}

// ---------- Agents (chat-view helpers, S4) ---------------------------------

// Sprint 15 — initial load returns the smart-window messages PLUS the
// `lastUserMessageId` (sticky anchor invariant) and `hasMore` (lets the
// paginated history hook stop at the agent's first message).
// Sprint 15 — composite-cursor pagination. The legacy `before` (timestamp
// only) is still accepted by the backend during the migration window but
// the preferred call uses `(beforeTs, beforeId)` so same-ms ties at the
// page boundary do not produce duplicates.
export interface FetchAgentMessagesResponse {
  messages: Message[];
  hasMore: boolean;
}

export function fetchAgentMessages(
  id: string,
  opts: { beforeTs?: string; beforeId?: string; limit?: number } = {},
): Promise<FetchAgentMessagesResponse> {
  const params = new URLSearchParams();
  if (opts.beforeTs) params.set('beforeTs', opts.beforeTs);
  if (opts.beforeId) params.set('beforeId', opts.beforeId);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return api<FetchAgentMessagesResponse>(
    `/api/agents/${encodeURIComponent(id)}/messages${qs ? `?${qs}` : ''}`,
  );
}

// Sprint 8 — multipart upload for one chip. Uses a raw fetch (not the
// `api()` helper) because we need multipart encoding + an AbortSignal
// hook + same auth/CSRF treatment.
export interface UploadAttachmentResp {
  chipId: string;
  route: 'image' | 'pdf' | 'text-inline' | 'disk';
  attachment: {
    id: string;
    route: 'image' | 'pdf' | 'text-inline' | 'disk';
    name: string;
    displayName: string;
    size: number;
    mimeType: string;
    sha256: string;
    diskPath?: string;
    pageCount?: number;
    textPreview?: string;
  };
}

export async function uploadAttachment(opts: {
  agentId: string;
  chipId: string;
  messageId: string;
  file: File;
  signal?: AbortSignal;
}): Promise<UploadAttachmentResp> {
  const base = getActiveBaseUrl();
  const url = `${base}/api/agents/${encodeURIComponent(opts.agentId)}/attachments`;
  const fd = new FormData();
  fd.append('chipId', opts.chipId);
  fd.append('messageId', opts.messageId);
  fd.append('displayName', opts.file.name);
  fd.append('file', opts.file);
  // No Content-Type header — the browser auto-sets the multipart boundary.
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { [CSRF_HEADER]: '1' },
    body: fd,
    signal: opts.signal,
    ...ACCESS_GATE_FETCH_INIT,
  });
  // D1/challenger R1 change 1 — this is a raw `fetch`, not `api()`; it must
  // run the SAME classification before its own error handling, or the
  // Access gate here would still degrade to a followed-redirect
  // `TypeError` mislabelled `NETWORK_ERROR`.
  if (isAccessGateRedirect(res)) {
    throw new ApiHttpError(0, 'ACCESS_GATE', 'Access gate redirect.');
  }
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as UploadAttachmentResp;
}

export function forkAgent(id: string, name?: string): Promise<{ agent: Agent }> {
  return api(`/api/agents/${encodeURIComponent(id)}/fork`, {
    method: 'POST',
    body: name ? { name } : {},
  });
}

export function deleteAgentRequest(id: string): Promise<void> {
  return api(`/api/agents/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Sprint 5.5 (B6): kills the underlying Claude process WITHOUT soft-deleting
// the agent row. Use for the Stop button in ChatHeader so the agent stays
// visible in the sidebar (and forkable) after stopping.
export function stopAgentRequest(id: string): Promise<void> {
  return api(`/api/agents/${encodeURIComponent(id)}/stop`, { method: 'POST' });
}

export function renameAgentRequest(
  id: string,
  name: string,
): Promise<{ agent: Agent }> {
  return api(`/api/agents/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { name },
  });
}

// Sprint 20.3 PR-6 (D013-EC-44 / EC-48) — manual failover. Triggered by
// the "Try another runner" button in the runner-offline banner. The
// backend picks a NEW runner via the scheduler (excluding the current
// one), atomically rewrites runner_id + previous_runner_id, persists the
// "🔁 Reasignado…" system message, and respawns with --resume so the
// chat continues seamlessly.
export interface FailoverAgentResponse {
  agent: Agent;
  failover: {
    fromRunnerId: string;
    toRunnerId: string;
    fromHostname: string | null;
    toHostname: string;
    systemMessageId: string;
  };
}

// Sprint move-runner-button (EC-135) — `toRunnerId` moves the
// conversation to a SPECIFIC server proactively (⋯-menu "Move to another
// server"), before hitting a cap. Omitted → the backend scheduler
// auto-picks, exactly as the banner's "Try another runner" always did.
export function failoverAgentRequest(
  id: string,
  toRunnerId?: string,
): Promise<FailoverAgentResponse> {
  return api(`/api/agents/${encodeURIComponent(id)}/failover`, {
    method: 'POST',
    body: toRunnerId ? { toRunnerId } : {},
  });
}

// ---------- Sprint 20.3 PR-8 — Admin runners panel ------------------------
//
// Wire-shape contract mirrors `RunnerSnapshot` in
// `backend/src/api/runners.routes.ts`. Re-declared here to keep the
// frontend free of backend imports (per S2 baseline convention).

export type RunnerStatus = 'online' | 'offline' | 'draining' | 'incompatible_version';

export type RunnerUnavailableReason =
  | 'claude_account_blocked'
  | 'disk_full'
  | 'claude_not_authenticated'
  | 'incompatible_version'
  // Sprint runner-resilience-after-missing-credential §2.4 — close the
  // backend↔frontend enum drift introduced by sprint
  // runner-credential-bootstrap. Backend has emitted this value since
  // migration 029, but this enum was never updated; when a runner
  // self-marked `missing_credential` the Settings page crashed with
  // "Cannot read properties of undefined (reading 'split')" because
  // `t(UNAVAILABLE_LABEL_KEY[runner.unavailableReason])` resolved to
  // `t(undefined)` and i18n's resolve() threw.
  | 'missing_credential'
  // Sprint runner-op-token-single-source (defect 3). Backend emits this
  // value (migration 036) when the runner's `op whoami` returns a
  // workspace URL other than the expected `santi-team.1password.com`.
  // Operator action required: rotate /etc/asoasis/op-token to a token
  // from the correct workspace.
  | 'wrong_workspace';

export interface RunnerSnapshot {
  id: string;
  hostname: string;
  status: RunnerStatus;
  protocolVersion: number;
  claudeAccountId: string | null;
  cap5hResidualPct: number | null;
  // Sprint 20.3 PR-10 (D17) — ISO 8601 timestamp at which the runner's
  // 5h rolling cap resets. Surfaced for the centralized usage modal's
  // "Earliest reset" footer. NULL when the runner has never sent a v3
  // heartbeat with the cap reset metadata.
  cap5hResetAt: string | null;
  weeklyHoursSonnet: number | null;
  weeklyHoursOpus: number | null;
  tpmHeadroomPct: number | null;
  activeAgents: number;
  diskPctUsed: number | null;
  // Sprint fleet-resource-metrics (spec §2 D7/D9) — latest-sample
  // CPU/RAM, mirrors `diskPctUsed`. NULL until the runner's first v3
  // heartbeat carrying the field lands. CPU has no upper bound (a
  // loaded box can read >100%); the UI clamps at render (D1).
  cpuPct: number | null;
  ramPctUsed: number | null;
  drainMode: boolean;
  unavailableReason: RunnerUnavailableReason | null;
  lastSeen: string | null;
  createdAt: string;
  updatedAt: string | null;
  // Sprint runner-checkout-freshness (ADR D-1073, VISIBILITY half) —
  // worst-repo checkout-freshness headline. NULL until the runner's
  // first v3 heartbeat carrying the field lands. Staleness is derived
  // from `checkoutCheckedAt` + `lastSeen` at render time, never stored
  // as a boolean (D-1035).
  checkoutBlockerReason: string | null;
  checkoutStalenessCommits: number | null;
  checkoutCheckedAt: string | null;
  checkoutBlockerRepo: string | null;
  // Sprint runner-claude-version-visibility — cached `claude --version`
  // (parsed leading semver). NULL until the runner's first heartbeat
  // carrying it lands, or when the runner cannot run the binary.
  claudeCliVersion: string | null;
}

export function fetchRunners(): Promise<{ runners: RunnerSnapshot[] }> {
  return api('/api/runners');
}

// Sprint fleet-resource-metrics (spec §2 D9) — rolling CPU/RAM/disk
// history for the Settings → Runners sparkline. `window` mirrors the
// backend's duration-string param (`'6h'` default, server-capped at
// `'24h'`).
export interface RunnerMetricsSample {
  sampledAt: string;
  cpuPct: number | null;
  ramPctUsed: number | null;
  diskPctUsed: number | null;
}

export function fetchRunnerMetrics(
  id: string,
  opts?: { window?: string },
): Promise<{ runnerId: string; windowMs: number; samples: RunnerMetricsSample[] }> {
  const params = new URLSearchParams();
  if (opts?.window) params.set('window', opts.window);
  const qs = params.size > 0 ? `?${params.toString()}` : '';
  return api(`/api/runners/${encodeURIComponent(id)}/metrics${qs}`);
}

// Sprint 20.8 (D013-EC-76) — wire shape returned by GET /api/runners/usage.
// One row per runner. `usage` carries the real per-bucket Anthropic
// payload from the runner (null on failure). `usageErrorCode` is set
// only when `usage === null`; on success it is omitted entirely.
export type RunnerUsageErrorCode =
  | 'OAUTH_TOKEN_MISSING'
  | 'OAUTH_TOKEN_INVALID'
  | 'RATE_LIMITED'
  | 'UPSTREAM_ERROR'
  | 'TIMEOUT'
  | 'RUNNER_OFFLINE'
  | 'BRIDGE_STOPPED'
  | 'BRIDGE_UNCONFIGURED';

export interface RunnerUsageEntry {
  runnerId: string;
  hostname: string;
  status: RunnerStatus;
  claudeAccountId: string | null;
  drainMode: boolean;
  lastSeen: string | null;
  // Sprint usage-last-known-good (D013-EC-172) D1 — `usage` means "the
  // last reading we hold", not "fetched just now": an error path carries
  // the previous good reading forward instead of blanking the panel.
  usage: UsageData | null;
  // When THESE numbers were taken. Distinct from the store's entry-level
  // `fetchedAtIso` (age of the last fleet-wide attempt) — feeding one where
  // the other belongs captions hours-old numbers as "hace 1 min", which is
  // worse than no caption. Optional on the type so an older backend still
  // type-checks against this client.
  usageAtIso?: string | null;
  // D4 — which silence this is. `not_asked`: we chose not to ask (armed
  // Retry-After window, or the runner is offline / bridge unconfigured).
  // `upstream_error`: we asked and it failed. Drives the muted line's copy.
  lastAttempt?: 'ok' | 'not_asked' | 'upstream_error';
  usageErrorCode?: RunnerUsageErrorCode;
  // Sprint usage-backoff-truth (D013-EC-148) — when this runner is
  // rate-limited, the instant we will ask Anthropic again. Optional: only
  // rate-limited rows carry it, and only from a backend new enough to
  // send it.
  usageRetryAtIso?: string;
  // Sprint runner-claude-version-visibility — cached `claude --version`
  // (parsed leading semver). NULL until the runner reports it. The
  // UsageModal reads THIS DTO, so the version rides here to reach the row.
  claudeCliVersion: string | null;
  // Sprint usage-from-stream (D3) + usage-fable-window-from-stream (D1/D3)
  // — per-window provenance + freshness stamp, mirroring the backend
  // `RunnerUsageEntry.windowSources` verbatim. Absent on every row until
  // a stream reading has won at least one window's merge for that
  // runner. `UsageAgeCaption.rpcBucketsUsable` is the ONE consumer that
  // gates rendering on this.
  windowSources?: Partial<
    Record<'five_hour' | 'seven_day' | 'seven_day_overage_included', { source: 'rpc' | 'stream'; atIso: string }>
  >;
}

// Sprint 20.8 (D013-EC-76) — modal payload now carries real Anthropic
// per-runner data via fan-out. Pre-S20.8 it returned RunnerSnapshot
// rows with the heuristic `cap_5h_residual_pct` / `weekly_hours_*`
// projection; that data still flows through `fetchRunners()` for the
// admin panel + scheduler ranking, but the modal switched to
// authoritative numbers.
export function fetchRunnersUsage(opts?: {
  signal?: AbortSignal;
  // Sprint rate-limit-banner-ssot — when true, bypasses the backend
  // TTL cache and forces a fresh fan-out to Anthropic. Used by the
  // lazy poll hook while the banner is active so we don't sit on a
  // stale 60 s window when ground truth has likely shifted.
  fresh?: boolean;
  // Sprint usage-backoff-truth (D013-EC-148) — founder-intent marker.
  // `fresh` alone only bypasses the backend's 60 s TTL; a runner inside
  // its Anthropic Retry-After window is still skipped, so the modal can
  // re-serve an hours-old RATE_LIMITED row without asking anyone.
  // `bypassBackoff` additionally skips that window, and is therefore set
  // ONLY where a human just asked (the usage modal opening / its retry
  // button). It is deliberately NOT set by the NetworkBanner's 60 s poll
  // below in the same module's consumers — that is a machine, and a
  // machine bypassing the window is pre-mortem F2 (up to 60 calls/h at a
  // runner Anthropic has already told us to leave alone).
  bypassBackoff?: boolean;
  // Sprint usage-single-emitter (D013-EC-166) — the MACHINE read mode.
  // The backend serves whatever its cache holds and never calls
  // Anthropic, however stale or absent the entry is. Every non-founder
  // surface uses it: the census in the sprint spec found four machine
  // emitters whose combined volume dwarfed the background warmer two
  // sprints in a row optimised. Mutually exclusive with `fresh` /
  // `bypassBackoff` by construction — those are founder-intent markers.
  cacheOnly?: boolean;
}): Promise<{
  runners: RunnerUsageEntry[];
  // Sprint usage-single-emitter (D013-EC-166) — when the control-plane
  // cache last reached Anthropic. `null` means it never has (cold control
  // plane, or a wiring with no cache at all). Optional on the type so an
  // older backend still type-checks against this client.
  fetchedAtIso?: string | null;
  stale?: boolean;
}> {
  const params = new URLSearchParams();
  if (opts?.fresh) params.set('force', 'true');
  if (opts?.bypassBackoff) params.set('bypassBackoff', 'true');
  if (opts?.cacheOnly) params.set('cacheOnly', 'true');
  const qs = params.size > 0 ? `?${params.toString()}` : '';
  return api(`/api/runners/usage${qs}`, { signal: opts?.signal });
}

export function drainRunner(id: string): Promise<{ runner: RunnerSnapshot; dispatchedToRunner: boolean }> {
  return api(`/api/runners/${encodeURIComponent(id)}/drain`, { method: 'POST' });
}

export function undrainRunner(id: string): Promise<{ runner: RunnerSnapshot }> {
  return api(`/api/runners/${encodeURIComponent(id)}/undrain`, { method: 'POST' });
}

export function clearRunnerUnavailable(id: string): Promise<{ runner: RunnerSnapshot }> {
  return api(`/api/runners/${encodeURIComponent(id)}/clear-unavailable`, { method: 'POST' });
}

// ---------- Agents (creation) ----------------------------------------------
//
// Sprint 5.6 — composer-first agent creation. `name` is optional; when
// omitted, the backend assigns DEFAULT_AGENT_NAME and fires Haiku-driven
// auto-rename in the background. `templateId` is gone (templates feature
// removed by ADR D-001).

export interface CreateAgentPayload {
  name?: string;
  prompt: string;
  model?: string;
  effort?: EffortLevel;
  // S6.5 — sets the new agent's mode at spawn time. Optional so existing
  // tests/fixtures keep compiling; backend defaults to 'agent'.
  permissionMode?: PermissionMode;
  // Sprint 11b.1 — workspace assignment.
  projectId?: string;
}

// Sprint 12 (D-008 §2.3) — "+" creates an empty agent row immediately
// (no first-prompt requirement). The backend creates the DB row and
// returns it; CLI spawn is deferred to the first user message.
export async function createEmptyAgent(opts?: {
  model?: string;
  effort?: string;
  permissionMode?: string;
  projectId?: string;
  runnerId?: string;
}): Promise<Agent> {
  const res = await api<{ agent: Agent }>('/api/agents/empty', {
    method: 'POST',
    body: opts ?? {},
  });
  return res.agent;
}

export async function createAgent(payload: CreateAgentPayload): Promise<Agent> {
  const res = await api<{ agent: Agent }>('/api/agents', {
    method: 'POST',
    body: payload,
  });
  return res.agent;
}

// Sprint 5.6 (B7) — mid-chat config patch. Triggers a transparent --resume
// respawn server-side; we just receive the new agent row.
// S6.5 — `permissionMode` joins the patch fields. Same respawn machinery,
// new spawn flag (`--permission-mode bypassPermissions` ↔ `plan`).
export async function patchAgentConfig(
  id: string,
  fields: { model?: string; effort?: EffortLevel; permissionMode?: PermissionMode },
): Promise<Agent> {
  const res = await api<{ agent: Agent }>(`/api/agents/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: fields,
  });
  return res.agent;
}

// Sprint 5.7 (D32) — Undo path. Backend accepts `deletedAt: null` only; any
// other value is rejected. Clearing the timestamp re-inserts the agent into
// `list()` and the sidebar repopulates via the `agent:updated` WebSocket
// event (store subscribers also run on the returned row as a fallback).
export async function undeleteAgent(id: string): Promise<Agent> {
  const res = await api<{ agent: Agent }>(`/api/agents/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { deletedAt: null },
  });
  return res.agent;
}

// S6.5 — permission resolution.
//
// Body shapes:
//   • Allow once / Deny:        { decision: 'allow' | 'deny', reason? }
//   • Allow always:             { decision: 'allow_always' }   ← inserts allowlist rule server-side
//   • AskUserQuestion answer:   { decision: 'allow', answers: { [questionText]: chosenLabel | string[] } }
//
// Backend returns 204 on success, 410 Gone if the request is already
// resolved (idempotent — UI treats that as "someone else clicked first").
export type PermissionDecisionInput =
  | { decision: 'allow'; reason?: string; answers?: Record<string, string | string[]> }
  | { decision: 'deny'; reason?: string }
  | { decision: 'allow_always'; reason?: string };

export async function resolvePermission(
  agentId: string,
  requestId: string,
  body: PermissionDecisionInput,
): Promise<void> {
  await api(
    `/api/agents/${encodeURIComponent(agentId)}/permissions/${encodeURIComponent(requestId)}`,
    { method: 'POST', body },
  );
}

// S6.5 — global allowlist surface (Settings → "Allowed forever"). Phase 6
// renders the list; today these helpers exist so PermissionCard's tests
// can stub them and Phase 6 doesn't have to refactor the API layer.
export interface AllowlistRule {
  id: string;
  toolName: string;
  pattern: string;
  createdAt: string;
  createdByAgentId: string | null;
}

export async function fetchAllowlistRules(): Promise<AllowlistRule[]> {
  const res = await api<{ rules: AllowlistRule[] }>('/api/permissions/allowlist');
  return res.rules;
}

export async function deleteAllowlistRule(id: string): Promise<void> {
  await api(`/api/permissions/allowlist/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// On-demand Claude Max usage — proxied through the backend to avoid
// exposing the OAuth token to the browser.
export interface UsageBucket {
  utilization: number;
  resets_at: string;
}

// Sprint usage-limits-v2 (2026-07-08) — canonical `limits[]` entry.
// Anthropic's `/api/oauth/usage` now surfaces every active limit
// (session / weekly_all / weekly_scoped / future kinds) here. Legacy
// buckets above stay as fallback (D2) when the runner reports v9 or
// the array is absent.
export interface UsageLimitEntry {
  kind: string;
  group?: string;
  percent: number;
  severity?: string;
  resets_at: string;
  modelName?: string;
  isActive?: boolean;
}

export interface UsageData {
  five_hour: UsageBucket | null;
  seven_day: UsageBucket | null;
  seven_day_sonnet: UsageBucket | null;
  seven_day_opus: UsageBucket | null;
  extra_usage: (UsageBucket & { spent_usd?: number; limit_usd?: number }) | null;
  limits?: UsageLimitEntry[];
  // Sprint usage-fable-window-from-stream (D3) — additive optional field,
  // mirrors the backend `MergedUsage`. Stream-sourced only (protocol
  // untouched); absent unless a stream reading has won this window for
  // the runner. Renders in `LegacyBars` (D4), never in `LimitsBars`.
  seven_day_overage_included?: UsageBucket | null;
}

// Sprint 6.7 (D36) — composer autocomplete. Returns the scored + category-
// filtered list from /api/workdir/index. Consumers (useAutocomplete) pass
// an AbortSignal so stale requests don't overwrite fresher ones.
export async function fetchWorkdirIndex(opts: {
  trigger: '@' | '/';
  q: string;
  limit?: number;
  signal?: AbortSignal;
}): Promise<WorkdirIndexResponse> {
  const params = new URLSearchParams();
  params.set('trigger', opts.trigger);
  if (opts.q) params.set('q', opts.q);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  return api<WorkdirIndexResponse>(
    `/api/workdir/index?${params.toString()}`,
    { signal: opts.signal },
  );
}
